import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

/**
 * Agent State Summary (2026-08-10) — the #1 supervisor state report.
 * Per-agent time-in-state (Ready / Talk in+out / Wrap / AUX) + Occupancy and
 * Availability %, sourced from agent_state_history. AUX detail lists per-reason
 * seconds. Occupancy = (talk+wrap)/(talk+wrap+ready); Availability =
 * (ready+talk)/logged-in.
 */
function fmtSecs(s) {
  s = Number(s) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
              : `${m}:${String(r).padStart(2, '0')}`;
}
const pct = (v) => (v == null ? '—' : `${v}%`);

export default function AgentStateSummary() {
  const [range, setRange, apiParams] = useRangeState('today');
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'agent-state-summary', apiParams],
    queryFn: () => reportsApi.agentStateSummary(apiParams),
    keepPreviousData: true,
  });

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Agent state summary</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="agent-state-summary" params={apiParams} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>
      <p className="text-xs text-surface-muted mb-3">
        Time in each state per agent. <strong>Occupancy</strong> = busy (talk+wrap) ÷ (busy+ready);
        <strong> Availability</strong> = (ready+talk) ÷ logged-in. Source: agent state history.
      </p>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <ReportTable
        headers={['extension','agent_name','logged_in_sec','ready_sec','talk_in_sec',
                  'talk_out_sec','wrap_sec','aux_sec','occupancy_pct','availability_pct','aux_detail']}
        rows={data?.rows || []}
        columnLabels={{
          extension: 'Ext', agent_name: 'Agent', logged_in_sec: 'Logged in',
          ready_sec: 'Ready', talk_in_sec: 'Talk (in)', talk_out_sec: 'Talk (out)',
          wrap_sec: 'Wrap', aux_sec: 'AUX', occupancy_pct: 'Occupancy',
          availability_pct: 'Availability', aux_detail: 'AUX breakdown',
        }}
        formatters={{
          logged_in_sec: fmtSecs, ready_sec: fmtSecs, talk_in_sec: fmtSecs,
          talk_out_sec: fmtSecs, wrap_sec: fmtSecs, aux_sec: fmtSecs,
          occupancy_pct: pct, availability_pct: pct,
        }}
      />
    </div>
  );
}
